import { Box, Stack, Text, useMantineTheme } from '@mantine/core';
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { ThecData } from '../types';
import { latest, pctChange, previous } from '../helpers';
import { getMultiGoalStatus, getThresholdGoalState } from '../../statusUtils';
import { CardShell } from './CardShell';
import { MetricRow } from './MetricRow';

export function AcademicCreditCard({ data }: { data: ThecData }) {
  const theme = useMantineTheme();
  const series = data.academicCredit;
  const cur = series.length > 0 ? latest(series) : null;
  const prev = previous(series);

  const allTimeTotal = data.academicCreditAllTime.creditYes + data.academicCreditAllTime.creditNo;
  const pctAllTime = allTimeTotal > 0 ? (data.academicCreditAllTime.creditYes / allTimeTotal) * 100 : 0;

  const parseQuarterLabel = (label: string): { year: number; quarter: 1 | 2 | 3 | 4 } | null => {
    const m1 = label.match(/^Q([1-4])\s+(\d{4})$/);
    if (m1) return { year: Number(m1[2]), quarter: Number(m1[1]) as 1 | 2 | 3 | 4 };
    const m2 = label.match(/^(\d{4})\s+Q([1-4])$/);
    if (m2) return { year: Number(m2[1]), quarter: Number(m2[2]) as 1 | 2 | 3 | 4 };
    return null;
  };

  const curMeta = parseQuarterLabel(cur?.quarter ?? '');
  const curYear = curMeta?.year ?? null;
  const curQuarterNum = curMeta?.quarter ?? null;

  const ytdAgg =
    curYear === null || curQuarterNum === null
      ? null
      : series
          .filter((p) => {
            const m = parseQuarterLabel(p.quarter);
            return m?.year === curYear && m.quarter <= curQuarterNum;
          })
          .reduce(
            (acc, p) => ({ yes: acc.yes + p.creditYes, no: acc.no + p.creditNo }),
            { yes: 0, no: 0 },
          );

  const prevYearYtdAgg =
    curYear === null || curQuarterNum === null
      ? null
      : series
          .filter((p) => {
            const m = parseQuarterLabel(p.quarter);
            return m?.year === curYear - 1 && m.quarter <= curQuarterNum;
          })
          .reduce(
            (acc, p) => ({ yes: acc.yes + p.creditYes, no: acc.no + p.creditNo }),
            { yes: 0, no: 0 },
          );

  const pctYtd =
    ytdAgg === null || ytdAgg.yes + ytdAgg.no === 0 ? null : (ytdAgg.yes / (ytdAgg.yes + ytdAgg.no)) * 100;
  const pctPrevYearYtd =
    prevYearYtdAgg === null || prevYearYtdAgg.yes + prevYearYtdAgg.no === 0
      ? null
      : (prevYearYtdAgg.yes / (prevYearYtdAgg.yes + prevYearYtdAgg.no)) * 100;

  const curPct = cur && (cur.creditYes + cur.creditNo) > 0 ? (cur.creditYes / (cur.creditYes + cur.creditNo)) * 100 : 0;
  const prevPct =
    prev && prev.creditYes + prev.creditNo > 0 ? (prev.creditYes / (prev.creditYes + prev.creditNo)) * 100 : null;
  const qoq = prevPct === null ? null : curPct - prevPct;
  const arrow = (n: number): '↑' | '↓' | '' => (n > 0 ? '↑' : n < 0 ? '↓' : '');
  const qoqText = qoq === null ? '—' : arrow(qoq) ? `${arrow(qoq)}${Math.abs(qoq).toFixed(2)}% QoQ` : '0.00% QoQ';

  const yoyPct = pctYtd === null || pctPrevYearYtd === null ? null : pctChange(pctYtd, pctPrevYearYtd);
  const yoyText = yoyPct === null ? '—' : arrow(yoyPct) ? `${arrow(yoyPct)}${Math.abs(yoyPct).toFixed(2)}% YoY` : '0.00% YoY';

  const goal = 80;
  const status = getMultiGoalStatus([
    getThresholdGoalState({ value: pctAllTime, goal, direction: 'min' }),
  ]);

  const tooltipShellStyle = {
    backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
    border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
    borderRadius: 10,
    color: theme.colorScheme === 'dark' ? '#fff' : '#000',
    padding: '10px 12px',
    maxWidth: 320,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
  } as const;

  const seriesYesTotal = series.reduce((sum, p) => sum + p.creditYes, 0);
  const seriesNoTotal = series.reduce((sum, p) => sum + p.creditNo, 0);
  const allTimeYes = data.academicCreditAllTime.creditYes;
  const allTimeNo = data.academicCreditAllTime.creditNo;
  const priorYes = Math.max(0, allTimeYes - seriesYesTotal);
  const priorNo = Math.max(0, allTimeNo - seriesNoTotal);
  let cumulativeYes = priorYes;
  let cumulativeNo = priorNo;
  const chartData = series.map((p) => {
    cumulativeYes += p.creditYes;
    cumulativeNo += p.creditNo;
    const total = cumulativeYes + cumulativeNo;
    const rate = total > 0 ? (cumulativeYes / total) * 100 : 0;
    return {
      quarter: p.quarter,
      creditYes: p.creditYes,
      creditNo: p.creditNo,
      cumulativeYes,
      cumulativeNo,
      creditRate: rate,
    };
  });
  const maxRate = Math.max(goal, ...chartData.map((p) => p.creditRate));

  return (
    <CardShell
      title="Academic Credit"
      subtitle={cur?.quarter ?? 'No data'}
      statusLabel={status.label}
      statusColor={status.color}
      statusTooltip={status.tooltip}
    >
      <Stack spacing="sm">
        <MetricRow
          items={[
            {
              label: 'This Quarter',
              labelTooltip: 'Percent receiving academic credit this quarter.',
              value: (
                <Text size="lg" weight={800}>
                  {Math.round(curPct)}%
                </Text>
              ),
              sublabel: qoqText,
            },
            {
              label: 'YTD',
              labelTooltip: 'Calendar year to date academic credit share (aggregated across quarters in the current year through this quarter).',
              value: (
                <Text size="lg" weight={800}>
                  {pctYtd === null ? '—' : `${Math.round(pctYtd)}%`}
                </Text>
              ),
              sublabel: yoyText,
            },
            {
              label: 'All-Time',
              labelTooltip: `Percent of participants receiving academic credit across all time (includes periods not shown). Goal: ${goal}%.`,
              value: (
                <Text size="lg" weight={800}>
                  {Math.round(pctAllTime)}%
                </Text>
              ),
              sublabel: `Goal ${goal}%`,
            },
          ]}
        />
        <Text size="xs" color="dimmed" weight={700} sx={{ letterSpacing: 0.2, marginTop: '0.75rem' }}>
          Cumulative Academic Credit
        </Text>
        <Box sx={{ height: 170 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.18} vertical={false} />
              <XAxis dataKey="quarter" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={12} />
              <YAxis
                orientation="right"
                tick={{ fontSize: 11 }}
                allowDecimals={false}
                axisLine={false}
                tickLine={false}
                width={44}
                tickMargin={8}
                domain={[0, Math.min(100, Math.ceil(maxRate))]}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload || payload.length === 0) return null;
                  const p: any = payload[0]?.payload;
                  if (!p) return null;
                  return (
                    <div style={tooltipShellStyle as any}>
                      <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>{label}</div>
                      <div style={{ fontSize: 12, lineHeight: 1.35 }}>
                        <div style={{ fontWeight: 900, fontSize: 14 }}>{Math.round(p.creditRate)}% Credit Rate</div>
                        <div style={{ opacity: 0.9 }}>
                          {p.cumulativeYes} yes out of {p.cumulativeYes + p.cumulativeNo} total
                        </div>
                      </div>
                    </div>
                  );
                }}
              />
              <ReferenceLine y={goal} stroke="#fa5252" strokeDasharray="4 4" ifOverflow="extendDomain" />
              <Line
                type="monotone"
                dataKey="creditRate"
                name="Cumulative Credit Rate"
                stroke={theme.colors.blue[6]}
                strokeWidth={2}
                dot={(dotProps: any) => {
                  const pointKey = dotProps?.payload?.quarter ?? dotProps?.index ?? `${dotProps?.cx}-${dotProps?.cy}`;
                  return (
                    <circle
                      key={`thec-academic-credit-dot-${pointKey}`}
                      cx={dotProps.cx}
                      cy={dotProps.cy}
                      r={2}
                      fill={theme.colors.blue[6]}
                      stroke={theme.colorScheme === 'dark' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)'}
                      strokeWidth={1.5}
                    />
                  );
                }}
                activeDot={{ r: 4 }}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Stack>
    </CardShell>
  );
}
